import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ClinqLogo } from "../components/ClinqLogo";
import { CheckCircle2, FileText, Users, Target, Handshake, ArrowRight } from "lucide-react";

const PILLARS = [
  { icon: FileText, title: "Investor materials built with you", body: "Financial model, pitch deck, information memorandum and data room — prepared by experienced analysts to the standard funders expect." },
  { icon: Users, title: "Investor engagement, overseen", body: "Targeting the right funders, managing outreach and Q&A, and keeping the process moving so you can keep running the business." },
  { icon: Target, title: "Accountability at every step", body: "A structured plan, weekly checkpoints and one team responsible for getting you to a term sheet." },
  { icon: Handshake, title: "All the way to close", body: "Support through due diligence, negotiation and closing — not just the introduction." },
];

export default function InvestorReadiness() {
  const [form, setForm] = useState({ full_name: "", email: "", company_name: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await api.post("/public/investor-readiness-interest", form);
      setDone(true);
    } catch (ex) {
      setErr(ex.response?.data?.detail || "Something went wrong — please try again.");
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-[#F5F7F7] text-neutral-900" data-testid="investor-readiness-page">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5">
        <Link to="/" data-testid="ir-logo-link"><ClinqLogo size="md" tone="teal" /></Link>
        <Link to="/login" className="text-sm font-bold text-[#0A8B85] hover:underline" data-testid="ir-login-link">Log in to Clinq</Link>
      </header>

      <section className="bg-[#0B2A28] px-5 py-14 text-white sm:py-20">
        <div className="mx-auto max-w-5xl">
          <span className="inline-block rounded-full border border-[#0A8B85]/60 bg-[#0A8B85]/20 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-[#7BD3CE]">Premium · Hands-on</span>
          <h1 className="mt-5 max-w-3xl text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl" data-testid="ir-headline">
            Personalised Investor Readiness Services
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#B9D9D6] md:text-lg">
            For founders who want dedicated, expert support: we work with a professional investor readiness firm whose
            analysts build your investor materials, oversee your investor engagement, and walk with you every step of the
            way — all the way to close.
          </p>
          <a href="#register" className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#0A8B85] px-7 py-3.5 text-sm font-bold text-white hover:bg-[#0C9F97]" data-testid="ir-hero-cta">
            Register your interest <ArrowRight size={16} />
          </a>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-12 sm:py-16">
        <div className="grid gap-5 sm:grid-cols-2">
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm">
              <Icon className="h-6 w-6 text-[#0A8B85]" />
              <p className="mt-3 text-base font-bold md:text-lg">{title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">{body}</p>
            </div>
          ))}
        </div>
        <p className="mt-8 rounded-xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
          This is a premium, fully managed service. Fees are agreed directly with the partner firm after a scoping call —
          registering your interest carries no obligation.
        </p>
      </section>

      <section id="register" className="mx-auto max-w-2xl px-5 pb-20">
        <div className="rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm sm:p-8" data-testid="ir-form-card">
          {done ? (
            <div className="text-center" data-testid="ir-success">
              <CheckCircle2 className="mx-auto h-10 w-10 text-[#0A8B85]" />
              <p className="mt-3 text-lg font-bold">Interest registered</p>
              <p className="mt-1 text-sm text-neutral-600">Thank you, {form.full_name.split(" ")[0]}. The Clinq Capital Team will be in touch within two working days to arrange a scoping conversation.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <p className="text-[11px] font-bold uppercase tracking-widest text-[#0A8B85]">Register your interest</p>
              <h2 className="text-base font-bold md:text-lg">Tell us who you are and we'll reach out</h2>
              {[["full_name", "Full name", "text", "Amina Wanjiru"], ["email", "Email address", "email", "you@business.com"], ["company_name", "Business name", "text", "Wanjiru Foods Ltd"]].map(([k, label, type, ph]) => (
                <label key={k} className="block">
                  <span className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{label}</span>
                  <input type={type} required value={form[k]} onChange={set(k)} placeholder={ph}
                    className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-[#0A8B85] focus:ring-2 focus:ring-[#0A8B85]/20"
                    data-testid={`ir-input-${k}`} />
                </label>
              ))}
              {err && <p className="text-sm text-red-600" data-testid="ir-error">{err}</p>}
              <button type="submit" disabled={busy}
                className="w-full rounded-full bg-[#0A8B85] px-6 py-3 text-sm font-bold text-white hover:bg-[#0C9F97] disabled:opacity-50"
                data-testid="ir-submit">
                {busy ? "Sending…" : "Register my interest"}
              </button>
              <p className="text-center text-[11px] text-neutral-500">We'll only use these details to contact you about this service.</p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
